/**
 * Loads the estimate behind an approval token and renders the page.
 *
 * This is the only screen in the product with **no session**. A customer
 * clicking a link from their email has no account and should never be asked
 * for one — requiring a sign-up to approve your own repair is how you lose the
 * approval. So the token is the whole credential, and resolving it happens
 * server-side under the service role, because RLS would correctly show a
 * stranger nothing at all.
 *
 * Everything shown here is what the SHOP issued: the lines, the totals and the
 * document hash are read back from the frozen version, never recomputed. A
 * customer must be looking at exactly the document the estimator sent, down to
 * the hash their approval is recorded against.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApprovalPage } from './ApprovalPage.js';
import type { EstimateDocument } from '../document/estimate.js';
import type { RecordedApproval } from './decision.js';

export { approvalToken } from './token.js';

/** What the Edge Function returns for a valid token. */
interface Resolved {
  reference: string;
  customer: string;
  vehicle: string;
  impact: string | null;
  version: number;
  hash: string;
  issuedAt: string | null;
  currencyCode: string;
  grandTotal: number;
  totals: Record<string, unknown>;
  lines: Array<{
    line_no: number; operation: string; description: string;
    part_number: string | null; part_price: string | number | null;
    quantity: number | null; hours: string | number | null;
    whyThis?: { observations: string[]; photos: string[] };
  }>;
  expiresAt: string;
  expired: boolean;
  existing: { choice: RecordedApproval['choice']; note: string | null; at: string } | null;
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/approval`;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function call(payload: Record<string, unknown>) {
  const res = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      // The anon key identifies the project, not the person. The token in the
      // body is what actually authorises anything.
      apikey: ANON,
      authorization: `Bearer ${ANON}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).error ?? `Something went wrong (${res.status}).`);
  return data;
}

/** The frozen version, as a document the page can render. */
function toDocument(r: Resolved): EstimateDocument {
  const t = r.totals as Record<string, number | undefined>;
  const row = (label: string, amount: number | undefined) =>
    (amount ? [{ label, amount }] : []);

  return {
    kind: 'invoice',
    title: 'Estimate',
    reference: r.reference,
    shopName: '',
    customerName: r.customer,
    terms: '',
    vehicle: r.vehicle,
    impact: r.impact,
    language: 'en',
    currencyCode: r.currencyCode,
    rows: r.lines.map((l, i) => {
      const unit = l.part_price === null ? null : Number(l.part_price);
      const qty = Number(l.quantity ?? 1);
      return {
        no: l.line_no ?? i + 1,
        operation: l.operation,
        description: l.description,
        partNumber: l.part_number,
        quantity: qty,
        unitPrice: unit,
        // A repair, an R&I or an inspection consumes no part. Showing "$0.00"
        // against those reads as free work rather than as labour-only.
        partless: !unit,
        hours: Number(l.hours ?? 0),
        amount: (unit ?? 0) * qty,
        suppressed: false,
        whyThis: l.whyThis,
      };
    }),
    totals: [
      ...row('Parts', t.partsTotal),
      ...row('Labour', t.labourTotal),
      ...row('Paint materials', t.paintMaterials),
      ...row('Shop materials', t.shopMaterials),
      ...((t.taxes as unknown as Array<{ name: string; amount: number }> | undefined) ?? [])
        .map((tx) => ({ label: tx.name, amount: tx.amount })),
    ],
    grandTotal: r.grandTotal,
    issued: r.issuedAt
      ? { at: r.issuedAt, version: r.version, hash: r.hash }
      : null,
    callToAction: 'Please tell us how to proceed.',
    suppressedNote: null,
  };
}

export function ApprovalRoute({ token }: { token: string }) {
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<RecordedApproval | null>(null);

  useEffect(() => {
    let live = true;
    call({ action: 'resolve', token })
      .then((d) => { if (live) setResolved(d as Resolved); })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e));
      });
    return () => { live = false; };
  }, [token]);

  const respond = useCallback((r: RecordedApproval) => {
    // Shown as answered straight away — the customer has decided, and a
    // spinner between the tap and the acknowledgement invites a second tap.
    setRecorded(r);
    call({ action: 'respond', token, choice: r.choice, note: r.note })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [token]);

  if (error) {
    return (
      <main className="approval-shell">
        <h1>We could not open that estimate</h1>
        <p>{error}</p>
        <p className="muted">
          If you were expecting to approve a repair, reply to the email you were
          sent and the shop will send a fresh link.
        </p>
      </main>
    );
  }

  if (!resolved) {
    return <main className="approval-shell"><p>Opening your estimate…</p></main>;
  }

  const existing: RecordedApproval | null = recorded ?? (resolved.existing
    ? {
        choice: resolved.existing.choice,
        note: resolved.existing.note ?? '',
        version: resolved.version,
        hash: resolved.hash,
        at: resolved.existing.at,
        next: resolved.existing.choice === 'approved' ? 'invoice'
          : resolved.existing.choice === 'partly' ? 'revise' : 'stop',
      }
    : null);

  return (
    <ApprovalPage
      doc={toDocument(resolved)}
      link={{
        // Both sides come from the same frozen version, so a link that was
        // sent for an older one is caught by the server before it gets here.
        sentFor: { version: resolved.version, hash: resolved.hash },
        current: { version: resolved.version, hash: resolved.hash },
        answeredAt: existing?.at ?? null,
        revoked: false,
        expiresAt: resolved.expiresAt,
        now: Date.now(),
      }}
      existing={existing}
      onRespond={respond}
    />
  );
}
